<template>
  <section class="admin-content" id="contact-search">
    <Navbar />
    <main class="admin-main">

      <!-- Page Header -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center">
              <div class="header-icon-wrap mr-3">
                <i class="fe fe-map-pin" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">Ward Manager</h4>
                <small style="opacity: .8;">
                  <span v-if="wards.length">{{ wards.length }} ward(s)</span>
                  <span v-else>Manage wards by LGA</span>
                </small>
              </div>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <button class="btn btn-light btn-sm" style="border-radius: 8px; font-weight: 600;" @click="show = !show">
                <i class="fe mr-1" :class="show ? 'fe-x' : 'fe-plus'"></i>
                {{ show ? 'Close Form' : 'Ward Form' }}
              </button>
              <router-link :to="{ path: '/lga/' + lga }" v-if="lga != null">
                <button class="btn btn-outline-light btn-sm" style="border-radius: 8px; font-weight: 600;">
                  <i class="fe fe-eye mr-1"></i> View Selected LGA
                </button>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="container mt-n3 pb-5">

        <!-- Ward Add / Edit form -->
        <div class="card table-card shadow mb-4" v-show="show">
          <div class="form-card-header">
            <div class="section-number"><i class="fe fe-plus" style="font-size:13px;"></i></div>
            <div>
              <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">
                {{ edit ? 'Edit Ward' : 'Add New Ward' }}
              </p>
            </div>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group col-md-12">
                <label class="info-label d-block mb-1">Select LGA</label>
                <select class="form-control" v-model="lga" @change="getWards">
                  <option v-for="lga in lga_states" v-bind:key="lga.id" :value="lga.id">
                    {{ lga.local_name }}
                  </option>
                </select>
              </div>
              <div class="form-group col-md-6">
                <label class="info-label d-block mb-1">Ward Name</label>
                <input type="text" required class="form-control" placeholder="Name of Ward"
                  v-model="register.ward_name" />
              </div>
              <div class="form-group col-md-6">
                <label class="info-label d-block mb-1">Ward Short Name</label>
                <input type="text" required class="form-control" placeholder="Short Name"
                  v-model="register.ward_short_name" />
              </div>
            </div>
            <button @click="submitForm" class="btn btn-submit px-4">
              <i class="fe fe-save mr-1"></i> {{ edit ? 'Update' : 'Submit' }}
            </button>
          </div>
        </div>

        <!-- LGA Selector -->
        <div class="card action-card shadow mb-4">
          <div class="card-body py-3 px-4">
            <label class="info-label d-block mb-1">Select LGA</label>
            <select class="form-control" v-model="lga" @change="getWards">
              <option v-for="lga in lga_states" v-bind:key="lga.id" :value="lga.id">
                {{ lga.local_name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Wards Table -->
        <div class="card table-card shadow mb-4">
          <div class="d-flex align-items-center px-4 pt-4 pb-3">
            <i class="fe fe-map-pin text-primary mr-2"></i>
            <h6 class="mb-0 font-weight-bold">Wards</h6>
          </div>
          <div class="table-responsive">
            <table class="table align-td-middle mb-0">
              <thead class="table-head">
                <tr>
                  <th class="table-th">Name</th>
                  <th class="table-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr class="table-row" v-for="ward in wards" v-bind:key="ward.id"
                  :class="{ 'table-active': selectedWard && selectedWard.id === ward.id }">
                  <td class="font-weight-bold">{{ ward.ward_name }}</td>
                  <td>
                    <div class="d-flex flex-wrap" style="gap:4px;">
                      <button class="btn btn-sm btn-outline-info" @click="selectWard(ward)"
                        :title="'Manage communities for ' + ward.ward_name">
                        <i class="fe fe-home mr-1"></i>Communities 
                      </button>
                      <router-link :to="{ path: '/ward/' + ward.id }">
                        <button class="btn btn-sm btn-outline-secondary"><i class="fe fe-eye"></i></button>
                      </router-link>
                      <button type="button" class="btn btn-sm btn-outline-primary" @click="editWard(ward)">
                        <i class="fe fe-edit"></i>
                      </button>
                      <button type="button" class="btn btn-sm btn-outline-danger" @click="deleteWard(ward)">
                        <i class="fe fe-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>
                <tr v-if="!wards || !wards.length">
                  <td colspan="2" class="text-center text-muted py-4">Select an LGA to view wards</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ── Communities Section ── -->
        <div v-if="selectedWard">

          <!-- Community Add/Edit Form -->
          <div class="card table-card shadow mb-4" v-show="showCommunity">
            <div class="form-card-header">
              <div class="section-number"><i class="fe fe-home" style="font-size:13px;"></i></div>
              <div>
                <p class="mb-0 text-white font-weight-bold" style="font-size:.95rem;">
                  {{ communityEdit ? 'Edit Community' : 'Add Community' }} — {{ selectedWard.ward_name }}
                </p>
              </div>
            </div>
            <div class="card-body">
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label class="info-label d-block mb-1">Community Name</label>
                  <input type="text" required class="form-control" placeholder="Community Name"
                    v-model="communityRegister.name" />
                </div>
              </div>
              <button @click="submitCommunityForm" class="btn btn-submit px-4">
                <i class="fe fe-save mr-1"></i> {{ communityEdit ? 'Update' : 'Submit' }}
              </button>
              <button type="button" class="btn btn-outline-secondary px-4 ml-2"
                @click="showCommunity = false; clearCommunity()">
                Cancel
              </button>
            </div>
          </div>

          <!-- Communities Table -->
          <div class="card table-card shadow mb-4">
            <div class="d-flex align-items-center justify-content-between px-4 pt-4 pb-3">
              <div class="d-flex align-items-center">
                <i class="fe fe-home text-info mr-2"></i>
                <h6 class="mb-0 font-weight-bold">
                  Communities — <span class="text-info">{{ selectedWard.ward_name }}</span>
                </h6>
                <span class="badge badge-pill badge-info ml-2">{{ communities.length }}</span>
              </div>
              <button class="btn btn-sm btn-info" style="border-radius: 8px; font-weight: 600;"
                @click="showCommunity = !showCommunity; communityEdit = false; clearCommunity()">
                <i class="fe mr-1" :class="showCommunity ? 'fe-x' : 'fe-plus'"></i>
                {{ showCommunity ? 'Close' : 'Add Community' }}
              </button>
            </div>
            <div class="table-responsive">
              <table class="table align-td-middle mb-0">
                <thead class="table-head">
                  <tr>
                    <th class="table-th">#</th>
                    <th class="table-th">Name</th>
                    <th class="table-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="table-row" v-for="(community, index) in communities" :key="community.id">
                    <td class="text-muted small">{{ index + 1 }}</td>
                    <td class="font-weight-bold">{{ community.name }}</td>
                    <td>
                      <div class="d-flex flex-wrap" style="gap:4px;">
                        <button type="button" class="btn btn-sm btn-outline-primary" @click="editCommunity(community)">
                          <i class="fe fe-edit"></i>
                        </button>
                        <button type="button" class="btn btn-sm btn-outline-danger" @click="deleteCommunity(community)">
                          <i class="fe fe-trash-2"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!communities.length">
                    <td colspan="3" class="text-center text-muted py-4">No communities for this ward yet</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
        <!-- ── End Communities Section ── -->

      </div>

      <div class="vld-parent">
        <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
      </div>
    </main>
  </section>
</template>

<script>
  import Navbar from "@/views/Navbar.vue";
  import Loading from "vue-loading-overlay";
  import "vue-loading-overlay/dist/vue-loading.css";

  export default {
    components: { Navbar, Loading },
    data() {
      return {
        user: null,
        lga_states: "",
        wards: "",
        ward_id: "",
        edit: false,
        show: false,
        isLoading: false,
        fullPage: true,
        agency_id: "",
        provider_id: "",
        lga: null,
        register: {
          ward_name: "",
          ward_short_name: "",
        },
        // Communities
        selectedWard: null,
        communities: [],
        communityEdit: false,
        showCommunity: false,
        community_id: "",
        communityRegister: {
          name: "",
        },
      };
    },
    beforeMount() {
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    methods: {
      // ── Wards ──
      getLGAs() {
        this.axios.get(`/api/v1/auth/lga/2679`)
          .then((response) => { this.lga_states = response.data.data; })
          .catch(console.error);
      },
      getWards() {
        this.selectedWard = null;
        this.communities = [];
        this.axios.get(`/api/v1/auth/getwards/${this.lga}`)
          .then((response) => { this.wards = response.data.data; })
          .catch(console.error);
      },
      submitForm() {
        this.edit ? this.updateWard() : this.addWard();
      },
      addWard() {
        this.isLoading = true;
        this.axios.post("/api/v1/auth/wards", {
          ward_name: this.register.ward_name,
          ward_short_name: this.register.ward_short_name,
          lg_id: this.lga,
          state_id: 2679,
        }).then(() => {
          this.isLoading = false;
          this.clearIt();
          this.getWards();
          this.$toasted.info("Ward Added", { position: "top-center", duration: 5000 });
        }).catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Error", { position: "top-left", duration: 5000 });
        });
      },
      editWard(ward) {
        this.show = true;
        this.edit = true;
        this.register.ward_name = ward.ward_name;
        this.register.ward_short_name = ward.ward_short_name;
        this.lga = ward.lg_id;
        this.ward_id = ward.id;
      },
      updateWard() {
        this.isLoading = true;
        this.axios.put(`/api/v1/auth/wards/${this.ward_id}`, {
          ward_name: this.register.ward_name,
          ward_short_name: this.register.ward_short_name,
          lg_id: this.lga,
          state_id: 2679,
        }).then(() => {
          this.isLoading = false;
          this.clearIt();
          this.edit = false;
          this.getWards();
          this.$toasted.info("Ward Updated Successfully", { position: "top-center", duration: 5000 });
        }).catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Error", { position: "top-left", duration: 5000 });
        });
      },
      deleteWard(ward) {
        if (!confirm("Are you sure you want to delete this ward?")) return;
        this.isLoading = true;
        this.axios.delete(`/api/v1/auth/wards/${ward.id}`)
          .then(() => {
            this.isLoading = false;
            this.edit = false;
            if (this.selectedWard && this.selectedWard.id === ward.id) {
              this.selectedWard = null;
              this.communities = [];
            }
            this.getWards();
            this.$toasted.info("Ward Deleted Successfully", { position: "top-center", duration: 5000 });
          }).catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            this.$toasted.error("Error", { position: "top-left", duration: 5000 });
          });
      },
      clearIt() {
        this.register.ward_name = "";
        this.register.ward_short_name = "";
      },

      // ── Communities ──
      selectWard(ward) {
        this.selectedWard = ward;
        this.showCommunity = false;
        this.clearCommunity();
        this.getCommunities(ward.id);
      },
      getCommunities(wardId) {
        this.isLoading = true;
        this.axios.get(`/api/v1/auth/communities`, {
          params: { ward_id: wardId }
        })
          .then((response) => {
            this.communities = response.data || [];
            this.isLoading = false;
          }).catch((error) => {
            console.error(error);
            this.isLoading = false;
          });
      },
      submitCommunityForm() {
        this.communityEdit ? this.updateCommunity() : this.addCommunity();
      },
      addCommunity() {
        if (!this.communityRegister.name.trim()) return;
        this.isLoading = true;
        this.axios.post("/api/v1/auth/communities", {
          name: this.communityRegister.name,
          ward_id: this.selectedWard.id,
        }).then(() => {
          this.isLoading = false;
          this.showCommunity = false;
          this.clearCommunity();
          this.getCommunities(this.selectedWard.id);
          this.$toasted.info("Community Added", { position: "top-center", duration: 5000 });
        }).catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Error adding community", { position: "top-left", duration: 5000 });
        });
      },
      editCommunity(community) {
        this.showCommunity = true;
        this.communityEdit = true;
        this.community_id = community.id;
        this.communityRegister.name = community.name;
      },
      updateCommunity() {
        if (!this.communityRegister.name.trim()) return;
        this.isLoading = true;
        this.axios.put(`/api/v1/auth/communities/${this.community_id}`, {
          name: this.communityRegister.name,
          ward_id: this.selectedWard.id,
        }).then(() => {
          this.isLoading = false;
          this.communityEdit = false;
          this.showCommunity = false;
          this.clearCommunity();
          this.getCommunities(this.selectedWard.id);
          this.$toasted.info("Community Updated", { position: "top-center", duration: 5000 });
        }).catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Error updating community", { position: "top-left", duration: 5000 });
        });
      },
      deleteCommunity(community) {
        if (!confirm(`Delete "${community.name}"?`)) return;
        this.isLoading = true;
        this.axios.delete(`/api/v1/auth/communities/${community.id}`)
          .then(() => {
            this.isLoading = false;
            this.getCommunities(this.selectedWard.id);
            this.$toasted.info("Community Deleted", { position: "top-center", duration: 5000 });
          }).catch((error) => {
            console.log(error.response);
            this.isLoading = false;
            this.$toasted.error("Error deleting community", { position: "top-left", duration: 5000 });
          });
      },
      clearCommunity() {
        this.communityRegister.name = "";
        this.community_id = "";
        this.communityEdit = false;
      },
    },
    created() {
      this.getLGAs();
    },
  };
</script>